import { createRouter, createWebHistory } from 'vue-router'
import AdminLayout from '../layouts/AdminLayout.vue'
import ShopLayout from '../layouts/ShopLayout.vue'
import SellerLayout from '../layouts/SellerLayout.vue'
import { tokenUtils } from '@/utils/auth.js'

// 청크 로딩 에러 재시도 함수 개선
function lazyLoadWithRetry(importFunc, maxRetries = 3) {
    return new Promise((resolve, reject) => {
        let retries = 0;

        function attempt() {
            importFunc()
                .then(resolve)
                .catch((error) => {
                    retries++;
                    console.warn(`🔄 청크 로딩 실패 (${retries}/${maxRetries}):`, error);

                    if (retries < maxRetries) {
                        const delay = Math.min(1000 * Math.pow(2, retries - 1), 5000);
                        console.log(`⏳ ${delay}ms 후 재시도...`);

                        setTimeout(attempt, delay);
                    } else {
                        console.error('❌ 청크 로딩 최종 실패:', error);
                        if ('caches' in window) {
                            caches.keys().then(function(names) {
                                for (let name of names) caches.delete(name);
                            });
                        }
                        window.location.reload(true);
                        reject(error);
                    }
                });
        }

        attempt();
    });
}

// 동적 import를 위한 래퍼 함수
const dynamicImport = (path) => () => lazyLoadWithRetry(() => import(path));

const routes = [

    // 관리자 로그인 페이지만 레이아웃 없이 분리
    {
        path: '/admin/login',
        name: 'AdminLogin',
        component: () => import('../views/admin/Login.vue')
    },

    // 입점사(셀러) 로그인 / 입점신청(가입) — 레이아웃 없이 분리
    {
        path: '/seller/login',
        name: 'SellerLogin',
        component: () => import('../views/seller/Login.vue')
    },
    {
        path: '/seller/apply',
        name: 'SellerApply',
        component: () => import('../views/seller/SellerApply.vue')
    },

    // 파트너 포털(영업사원) — 2026-08-21 신규. 레이아웃 없이 완전 분리
    {
        path: '/partner/login',
        name: 'PartnerLogin',
        component: () => import('../views/partner/Login.vue')
    },
    {
        path: '/partner/dashboard',
        name: 'PartnerDashboard',
        component: () => import('../views/partner/Dashboard.vue')
    },
    {
        path: '/partner/vendors',
        name: 'PartnerVendors',
        component: () => import('../views/partner/Vendors.vue')
    },
    {
        path: '/partner/orders',
        name: 'PartnerOrders',
        component: () => import('../views/partner/Orders.vue')
    },
    {
        path: '/partner/ledger',
        name: 'PartnerLedger',
        component: () => import('../views/partner/Ledger.vue')
    },
    {
        path: '/partner/settlements',
        name: 'PartnerSettlements',
        component: () => import('../views/partner/Settlements.vue')
    },
    {
        path: '/partner',
        redirect: '/partner/login'
    },

    // 입점사(셀러) 포털 영역
    {
        path: '/seller',
        component: SellerLayout,
        children: [
            { path: '', name: 'seller-dashboard', component: () => import('../views/seller/Dashboard.vue') },
            { path: 'products', name: 'seller-products', component: () => import('../views/seller/product/ProductList.vue') },
            { path: 'products/register', name: 'seller-product-register', component: () => import('../views/seller/product/ProductRegister.vue') },
            { path: 'products/bulk', name: 'seller-product-bulk', component: () => import('../views/seller/product/BulkUpload.vue') },
            { path: 'products/category-mapping', name: 'seller-category-mapping', component: () => import('../views/seller/product/CategoryMapping.vue') },
            { path: 'orders', name: 'seller-orders', component: () => import('../views/seller/orders/OrderList.vue') },
            { path: 'claims', name: 'seller-claims', component: () => import('../views/seller/orders/ClaimList.vue') },
            { path: 'shipping', name: 'seller-shipping', component: () => import('../views/seller/shipping/ShippingList.vue') },
            { path: 'shipping/invoice', name: 'seller-shipping-invoice', component: () => import('../views/seller/shipping/InvoiceRegister.vue') },
            { path: 'shipping/policy', name: 'seller-shipping-policy', component: () => import('../views/seller/shipping/ShippingPolicy.vue') },
            { path: 'settlement', name: 'seller-settlement', component: () => import('../views/seller/settlement/SettlementList.vue') },
            { path: 'settlement/tax', name: 'seller-settlement-tax', component: () => import('../views/seller/settlement/TaxInvoice.vue') },
            { path: 'store/settings', name: 'seller-store-settings', component: () => import('../views/seller/store/StoreSettings.vue') },
            { path: 'store/display', name: 'seller-store-display', component: () => import('../views/seller/store/StoreDisplay.vue') },
            { path: 'stats', name: 'seller-stats', component: () => import('../views/seller/stats/SalesReport.vue') },
            { path: 'notifications', name: 'seller-notifications', component: () => import('../views/seller/support/Notifications.vue') },
            { path: 'ads', name: 'seller-ads', component: () => import('../views/seller/ads/AdRequest.vue') },
            { path: 'inquiries', name: 'seller-inquiries', component: () => import('../views/seller/support/InquiryList.vue') },
            { path: 'community', name: 'seller-community', component: () => import('../views/seller/support/Community.vue') },
            { path: 'account', name: 'seller-account', component: () => import('../views/seller/account/SellerAccount.vue') }
        ]
    },

    // 사용자 리다이렉션
    // {
    //     path: '/',
    //     redirect: '/login'
    // },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/shop/Login.vue')
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => import('../views/shop/account/signup.vue')
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('../views/shop/account/ResetPassword.vue')
    },
    // 쇼핑몰 (사용자) 영역 라우트
    {
        path: '/',
        component: ShopLayout,
        children: [
            {
                path: 'home',
                name: 'shop-home',
                component: () => import('../views/shop/Home.vue')
            },
            {
                path: 'products',
                name: 'shop-products',
                component: () => import('../views/shop/product/ProductListView.vue')
            },
            {
                path: 'product/:id',
                name: 'product-detail',
                component: () => import('../views/shop/product/ProductDetailView.vue')
            },
            {
                path: 'store/:sellerId',
                name: 'seller-store',
                component: () => import('../views/shop/store/SellerStorePage.vue')
            },
            {
                path: 'cart',
                name: 'cart',
                component: () => import('../views/shop/cart/CartPage.vue')
            },
            {
                path: 'checkout',
                name: 'checkout',
                component: () => import('../views/shop/checkout/CheckoutView.vue')
            },
            {
                path: 'ordercomplete/:orderId',
                name: 'ordercomplete',
                component: () => import('../views/shop/ordercomplete/OrderComplete.vue'),
                props: true
            },
            {
                path: '/payment/toss/checkout',
                name: 'TossCheckout',
                component: () => import('@/views/shop/payment/TossCheckout.vue')
            },
            {
                path: '/payment/success',
                name: 'PaymentSuccess',
                component: () => import('@/views/shop/ordercomplete/OrderComplete.vue')
            },
            {
                path: '/checkout/success',
                name: 'CheckoutSuccess',
                component: () => import('@/views/shop/payment/BrandpaySuccess.vue')
            },
            {
                path: '/checkout/fail',
                name: 'CheckoutFail',
                component: () => import('@/views/shop/payment/PaymentFail.vue')
            },
            {
                path: '/payment/fail',
                name: 'PaymentFail',
                component: () => import('@/views/shop/payment/PaymentFail.vue')
            },
            {
                path: '/new-products',
                name: 'new-products',
                component: () => import('../views/shop/product/NewProductsPage.vue')
            },
            {
                path: '/promotions',
                name: 'promotions',
                component: () => import('../views/shop/product/PromotionsPage.vue')
            },
            {
                path: '/promotions/:id',
                name: 'promotion-detail',
                component: () => import('../views/shop/product/PromotionDetailPage.vue') // 이 페이지는 별도로 구현해야 합니다
            },
            {
                path: '/points-zone',
                name: 'points-zone',
                component: () => import('../views/shop/point/PointsZonePage.vue')
            },
            {
                path: 'mypage',
                name: 'mypage',
                component: () => import('../views/shop/mypage/Mypagemain.vue')
            },
            {
                path: 'mypage/orderhistory',
                name: 'orderhistory',
                component: () => import('../views/shop/mypage/OrderHistory.vue')
            },
            {
                path: 'mypage/return',
                name: 'return',
                component: () => import('../views/shop/mypage/ReturnRequest.vue')
            },
            {
                path: 'mypage/delivery',
                name: 'delivery',
                component: () => import('../views/shop/mypage/Delivery.vue')
            },
            {
                path: 'mypage/wishlist',
                name: 'wishlist',  // 'steamed'에서 'wishlist'로 변경
                component: () => import('../views/shop/mypage/Wishlist.vue')
            },
            {
                path: '/mypage/reorder/:orderNumber',
                name: 'reorder',
                component: () => import('@/views/shop/mypage/Reorder.vue'),
                meta: {
                    alwaysRefresh: true
                }
            },
            {
                path: 'mypage/ledger',
                name: 'ledger',
                component: () => import('../views/shop/mypage/LedgerPage.vue')
            },
            {
                // 거래처 원장 (가나전산 연동) — 기존 원장조회와 별개 화면
                path: 'mypage/gana-ledger',
                name: 'gana-ledger',
                component: () => import('../views/shop/mypage/GanaLedgerPage.vue')
            },
            {
                path: 'mypage/cash',
                name: 'doctor-cash',
                component: () => import('../views/shop/mypage/DoctorCashPage.vue')
            },
            {
                path: 'mypage/coupons',
                name: 'coupons',
                component: () => import('../views/shop/mypage/CouponsPage.vue')
            },
            {
                path: 'mypage/deposits',
                name: 'deposits',
                component: () => import('../views/shop/mypage/DepositPage.vue')
            },
            {
                path: 'mypage/points',
                name: 'points',
                component: () => import('../views/shop/mypage/PointsPage.vue')
            },
            {
                path: 'mypage/profile',
                name: 'profile',
                component: () => import('../views/shop/account/EditProfile.vue')
            },
            {
                path: 'mypage/payment-methods',
                name: 'paymentMethods',
                component: () => import('../views/shop/mypage/PaymentMethodsPage.vue')
            },
            {
                path: 'test/brandpay',
                name: 'brandpayTest',
                component: () => import('../views/shop/test/BrandpayTest.vue')
            },
            {
                path: 'test/brandpay/checkout',
                name: 'brandpayCheckout',
                component: () => import('../views/shop/test/BrandpayCheckout.vue')
            },
            {
                path: 'test/brandpay/success',
                name: 'brandpaySuccess',
                component: () => import('../views/shop/test/BrandpaySuccess.vue')
            },
            {
                path: 'test/brandpay/fail',
                name: 'brandpayFail',
                component: () => import('../views/shop/test/BrandpayFail.vue')
            },
            {
                // 🔥 브랜드페이 콜백 전용 페이지 (URL 파라미터를 sessionStorage에 저장 후 메인 페이지로 리다이렉트)
                path: 'mypage/payment-methods/callback',
                name: 'brandpayCallback',
                component: () => import('../views/shop/mypage/BrandpayCallback.vue')
            },
            {
                path: 'mypage/notification-settings',
                name: 'notificationSettings',
                component: () => import('../views/shop/account/NotificationSettingsPage.vue')
            },
            {
                path: 'mypage/withdrawal',
                name: 'withdrawal',
                component: () => import('../views/shop/account/WithdrawalPage.vue')
            },
            {
                path: '/mypage/faq',
                name: 'mypage-faq',
                component: () => import('../views/shop/support/FaqPage.vue')
            },
            {
                path: '/mypage/notices',
                name: 'mypage-notices',
                component: () => import('../views/shop/support/NoticesPage.vue')
            },
            {
                path: '/mypage/notices/:id',
                name: 'mypage-notice-detail',
                component: () => import('../views/shop/support/NoticeDetailPage.vue')
            },
            {
                path: '/mypage/inquiries',
                name: 'mypage-inquiries',
                component: () => import('../views/shop/support/InquiriesPage.vue')
            },
            {
                path: '/mypage/inquiries/create',
                name: 'mypage-inquiry-create',
                component: () => import('../views/shop/support/CreateInquiryPage.vue')
            },
            {
                path: '/mypage/inquiries/edit/:id',  // 🔥 이 라우트가 있는지 확인
                name: 'mypage-inquiry-edit',
                component: () => import('../views/shop/support/EditInquiryPage.vue')
            },
            {
                path: '/mypage/inquiries/:id',
                name: 'mypage-inquiry-detail',
                component: () => import('../views/shop/support/InquiryDetailPage.vue')
            }
        ]
    },

    // 관리자 영역 라우트
    {
        path: '/admin',
        component: AdminLayout,
        children: [

            {
                path: '', // 기본 대시보드는 남겨두고
                name: 'admin-home',
                component: () => import('../views/admin/DashboardHome.vue')
            },

            // 🔥 주문 관리 관련 라우트들 추가
            {
                path: 'shoporders',
                name: 'shoporders',
                component: () => import('../views/admin/shoporders/OrdersList.vue')
            },
            {
                path: 'shoporders/new',
                name: 'newOrders',
                component: () => import('../views/admin/shoporders/NewOrders.vue')
            },
            {
                path: 'shoporders/processing',
                name: 'processingOrders',
                component: () => import('../views/admin/shoporders/ProcessingOrders.vue')
            },
            {
                path: 'shoporders/preparing',
                name: 'preparingShipping',
                component: () => import('../views/admin/shoporders/PreparingShipping.vue')
            },
            {
                path: 'shoporders/shipping',
                name: 'shipping',
                component: () => import('../views/admin/shoporders/Shipping.vue')
            },
            {
                path: 'shoporders/delivered',
                name: 'delivered',
                component: () => import('../views/admin/shoporders/Delivered.vue')
            },
            {
                path: 'shoporders/cancelled',
                name: 'cancelledOrders',
                component: () => import('../views/admin/shoporders/CancelledOrders.vue')
            },

            {
                path: 'products',
                name: 'admin-products',
                component: () => import('../views/admin/ProductManagement.vue')
            },
            //디자인관리
            {
                path: 'design/main',
                name: 'design-main',
                component: () => import('../views/admin/design/MainProductDisplay.vue')
            },
            {
                path: 'design/theme',
                name: 'design-theme',
                component: () => import('../views/admin/design/ThemeLayoutManager.vue')
            },
            {
                path: 'design/banner',
                name: 'design-banner',
                component: () => import('../views/admin/design/BannerManager.vue')
            },
            {
                path: 'design/menu',
                name: 'design-menu',
                component: () => import('../views/admin/design/MenuManager.vue')
            },
            {
                path: 'design/branding',
                name: 'design-branding',
                component: () => import('../views/admin/design/LogoBrandingManager.vue')
            },
            {
                path: 'design/mobile',
                name: 'design-mobile',
                component: () => import('../views/admin/design/MobileDesignManager.vue')
            },
            {
                path: 'design/popup',
                name: 'design-popup',
                component: () => import('../views/admin/design/PopupManager.vue')
            },
            //환경설정- 기본설정
            {
                path: 'config/basic',
                name: 'config-basic',
                component: () => import('../views/admin/config/basic.vue')
            },
            //환경설정- 사용자 관리
            {
                path: 'config/users',
                name: 'config-users',
                component: () => import('../views/admin/config/users.vue')
            },
            //환경설정- 권한설정
            {
                path: 'config/permissions',
                name: 'config-permissions',
                component: () => import('../views/admin/config/permissions.vue')
            },
            //환경설정- 시스템설정
            {
                path: 'config/system',
                name: 'config-system',
                component: () => import('../views/admin/config/system.vue')
            },
            //환경설정- 휴일 관리 (공공데이터 공휴일 + 자체 휴일, 헤더 낮밤 배너 연동)
            {
                path: 'config/holidays',
                name: 'config-holidays',
                component: () => import('../views/admin/config/holidays.vue')
            },
            //환경설정- 알림 관리 (상황별 알림톡/문자/메일 자동발송) — 2026-08-24 신규
            {
                path: 'config/notifications',
                name: 'config-notifications',
                component: () => import('../views/admin/config/notifications.vue')
            },

            // 거래처 관리 - 거래처 목록
            {
                path: 'vendor/list',
                name: 'vendor-list',
                component: () => import('../views/admin/vendor/VendorList.vue')
            },
            // 거래처 관리 - 거래처 등록
            {
                path: 'vendor/register',
                name: 'vendor-register',
                component: () => import('../views/admin/vendor/register.vue')
            },
            // 거래처 관리 - 영업사원 파트너 (2026-08-21 신규)
            {
                path: 'partner/list',
                name: 'admin-partner-list',
                component: () => import('../views/admin/partner/PartnerList.vue')
            },
            // 거래처 관리 - 예상 정산 내역 (2026-08-21 신규)
            {
                path: 'partner/settlements',
                name: 'admin-partner-settlements',
                component: () => import('../views/admin/partner/PartnerSettlement.vue')
            },
            // 거래처 관리 - 거래처 등급
            {
                path: 'vendor/level',
                name: 'vendor-level',
                component: () => import('../views/admin/vendor/level.vue')
            },
            // 거래처 관리 - 거래처 거래내역
            {
                path: 'vendor/transactions',
                name: 'vendor-transactions',
                component: () => import('../views/admin/vendor/transactions.vue')
            },
            // 거래처 관리 - 구매수량 제한
            {
                path: 'vendor/purchase-limit',
                name: 'vendor-purchase-limit',
                component: () => import('../views/admin/vendor/PurchaseLimit.vue')
            },
            // 거래처 관리 - 초대링크 관리 (구회원 SNS 캠페인 1회성)
            {
                path: 'invite-tokens',
                name: 'admin-invite-tokens',
                component: () => import('../views/admin/invite/InviteTokenManager.vue')
            },
            // 제품 관리 - 제품 관리 경로 추가
            {
                path: 'product/list',
                name: 'product-list',
                component: () => import('../views/admin/product/ProductList.vue')
            },
            // 제품 관리 - 제품 등록 경로 추가
            {
                path: 'product/register',
                name: 'product-register',
                component: () => import('../views/admin/product/Productregister.vue')
            },
            // 제품 관리 - 카테고리 경로 추가
            {
                path: 'product/categories',
                name: 'product-categories',
                component: () => import('../views/admin/product/Productcategories.vue')
            },
            // 제품 관리 - 제품 순서 정렬
            {
                path: 'product/sort-order',
                name: 'product-sort-order',
                component: () => import('../views/admin/product/ProductSortOrder.vue')
            },
            // 재고관리 - 재고현황
            {
                path: 'inventory/status',
                name: 'inventory-status',
                component: () => import('../views/admin/inventory/InventoryStatus.vue')
            },
            // 🔥 재고관리 - 재고 조정 (추가)
            {
                path: 'inventory/adjust',
                name: 'inventory-adjust',
                component: () => import('../views/admin/inventory/InventoryStatus.vue')
            },
            // 🔥 재고관리 - 가나전산 연동 데이터 조회 (조회 전용, DB 미변경)
            {
                path: 'inventory/gana',
                name: 'inventory-gana',
                component: () => import('../views/admin/erp/GanaErpViewer.vue')
            },
            // 🔥 재고관리 - 용마로지스 배송조회 (조회 전용, DB 미변경) — 2026-08-25 신규
            {
                path: 'inventory/yongma',
                name: 'inventory-yongma',
                component: () => import('../views/admin/erp/YongmaViewer.vue')
            },
            // 재고관리 - 입고 관리
            {
                path: 'inventory/incoming',
                name: 'inventory-incoming',
                component: () => import('../views/admin/inventory/InventoryIncoming.vue')
            },
            // 재고관리 - 출고 관리
            {
                path: 'inventory/outgoing',
                name: 'inventory-outgoing',
                component: () => import('../views/admin/inventory/InventoryOutbound.vue')
            },
            // 주문 관리 - 주문 목록
            {
                path: 'shoporders/order',
                name: 'shoporders-order',
                component: () => import('../views/admin/shoporders/OrdersList.vue')
            },
            // 주문 관리 - 신규 주문
            {
                path: 'shoporders/neworder',
                name: 'shoporders-neworder',
                component: () => import('../views/admin/shoporders/NewOrders.vue')
            },
            // 주문 관리 - 주문 처리
            // 주문 관리 - 주문 완료
            // 주문 관리 - 주문 취소
            // 주문 관리 - 결제 확인

            // 수주/견적관리 - 수주 관리
            {
                path: 'business/list',
                name: 'business-list',
                component: () => import('../views/admin/business/OrderList.vue')
            },
            // 수주/견적관리 - 견적 목록
            {
                path: 'business/estimates',
                name: 'business-estimates',
                component: () => import('../views/admin/business/Estimates.vue')
            },
            // 수주/견적관리 - 견적 등록
            {
                path: 'business/create',
                name: 'business-create',
                component: () => import('../views/admin/business/EstimatesCreate.vue')
            },
            // 배송/출고관리 - 배송현황
            {
                path: 'shipping/list',
                name: 'shipping-list',
                component: () => import('../views/admin/shipping/ShippingList.vue')
            },
            // 배송/출고관리 - 출고현황
            {
                path: 'shipping/dispatch',
                name: 'shipping-dispatch',
                component: () => import('../views/admin/shipping/DispatchList.vue')
            },
            // 배송/출고관리 - 송장등록
            {
                path: 'shipping/invoice',
                name: 'shipping-invoice',
                component: () => import('../views/admin/shipping/InvoiceRegis.vue')
            },
            // 프로모션 관리 경로 추가
            {
                path: 'promotion/list',
                name: 'promotion-list',
                component: () => import('../views/admin/promotion/PromotionList.vue')
            },
            {
                path: 'promotion/coupons',
                name: 'promotion-coupons',
                component: () => import('../views/admin/promotion/CouponManager.vue')
            },
            // 정산및 외상결제관리
            {
                path: 'payment/settlements',
                name: 'payment-settlements',
                component: () => import('../views/admin/payment/Settlements.vue')
            },
            //  외상결제현황
            {
                path: 'payment/deferred',
                name: 'payment-deferred',
                component: () => import('../views/admin/payment/Deferred.vue')
            },
            //  입금내역
            {
                path: 'payment/deposits',
                name: 'payment-deposits',
                component: () => import('../views/admin/payment/Deposits.vue')
            },
            // 포인트 관리
            {
                path: 'payment/point-management',
                name: 'point-management',
                component: () => import('../views/admin/payment/PointManagement.vue')
            },
            // 포인트 내역
            {
                path: 'payment/point-history',
                name: 'point-history',
                component: () => import('../views/admin/payment/PointHistory.vue')
            },
            // 포인트 설정
            {
                path: 'payment/point-settings',
                name: 'point-settings',
                component: () => import('../views/admin/payment/PointSettings.vue')
            },
            // 매출/통계
            {
                path: 'stats/sales',
                name: 'stats-sales',
                component: () => import('../views/admin/stats/SalesList.vue')
            },
            // 주문통계
            {
                path: 'stats/statsorders',
                name: 'stats-statsorders',
                component: () => import('../views/admin/stats/StatsOrders.vue')
            },
            // 고객분석
            {
                path: 'stats/customers',
                name: 'stats-customers',
                component: () => import('../views/admin/stats/CustomerStats.vue')
            },
            {
                path: 'support/dashboard',
                name: 'SupportDashboard',
                component: () => import('@/views/admin/support/SupportDashboard.vue')
            },
            {
                path: 'support/notice',
                name: 'NoticeManagement',
                component: () => import('@/views/admin/support/NoticeManagement.vue')
            },
            {
                path: 'support/faq',
                name: 'FaqManagement',
                component: () => import('@/views/admin/support/FaqManagement.vue')
            },
            {
                path: 'support/inquiry',
                name: 'InquiryManagement',
                component: () => import('@/views/admin/support/InquiryManagement.vue')
            },
            {
                path: 'support/reviews',
                name: 'ReviewManagement',
                component: () => import('@/views/admin/support/ReviewManagement.vue')
            },
            {
                path: 'support/feedback',
                name: 'FeedbackManagement',
                component: () => import('@/views/admin/support/FeedbackManagement.vue')
            },

            // ===== 입점사 관리 (마켓플레이스) =====
            // 입점사 목록 / 수수료·택배비 설정(일괄·개별)
            {
                path: 'seller/list',
                name: 'admin-seller-list',
                component: () => import('@/views/admin/seller/SellerList.vue')
            },
            // 입점신청(가입) 승인
            {
                path: 'seller/signups',
                name: 'admin-seller-signups',
                component: () => import('@/views/admin/seller/SignupApproval.vue')
            },
            // 상품 승인 (승인대기/승인/취소·반려)
            {
                path: 'seller/product-approval',
                name: 'admin-seller-product-approval',
                component: () => import('@/views/admin/seller/ProductApproval.vue')
            },
            // 입점사 스토어(미니샵) 승인
            {
                path: 'seller/store-approval',
                name: 'admin-seller-store-approval',
                component: () => import('@/views/admin/seller/StoreApproval.vue')
            },
            // 입점사 광고 관리 (셀러 광고신청 승인/반려)
            {
                path: 'seller/ads',
                name: 'admin-seller-ads',
                component: () => import('@/views/admin/seller/AdManagement.vue')
            },
            // 입점사 문의 (셀러→본사 문의 답변)
            {
                path: 'seller/inquiries',
                name: 'admin-seller-inquiries',
                component: () => import('@/views/admin/seller/SellerInquiry.vue')
            },
            // 입점사 공지 관리 (본사 작성 → 입점사 열람)
            {
                path: 'seller/notices',
                name: 'admin-seller-notices',
                component: () => import('@/views/admin/seller/SellerNotice.vue')
            },
            // 입점사별 매출통계 + 랭킹
            {
                path: 'seller/sales-stats',
                name: 'admin-seller-sales-stats',
                component: () => import('@/views/admin/seller/SellerSalesStats.vue')
            },
            // 입점사 판매 정산
            {
                path: 'seller/settlement',
                name: 'admin-seller-settlement',
                component: () => import('@/views/admin/seller/SellerSettlement.vue')
            }
            // 아래 경로들은 나중에 각 파일을 만든 후 주석 해제
            // {
            //   path: 'categories',
            //   name: 'admin-categories',
            //   component: () => import('../views/admin/CategoryManagement.vue')
            // },
            // {
            //   path: 'orders',
            //   name: 'admin-orders',
            //   component: () => import('../views/admin/OrderManagement.vue')
            // },
            // {
            //   path: 'users',
            //   name: 'admin-users',
            //   component: () => import('../views/admin/UserManagement.vue')
            // }
        ]
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    // 🔥 페이지 이동 시 스크롤 처리
    scrollBehavior(to, from, savedPosition) {
        // 🔥 상품 목록 페이지는 스크롤 처리 완전히 제외 (ProductListView에서 자체 처리)
        const isProductListPage = to.path === '/products' || to.name === 'shop-products';
        if (isProductListPage) {
            return false; // 스크롤 변경 안 함
        }

        // 🔥 브라우저 뒤로가기/앞으로가기 시 savedPosition 사용
        if (savedPosition) {
            return savedPosition;
        }

        return { top: 0 };
    }
})
// 여기에 라우터 가드 추가
router.beforeEach((to, from, next) => {
    // 🔥 사용자: sessionStorage만 사용 (브라우저 닫으면 로그아웃)
    // 🔥 관리자: localStorage 또는 sessionStorage 사용 (로그인 유지)
    let userToken = sessionStorage.getItem('auth_token');
    let adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');

    // 🔥 토큰 만료 확인 및 처리
    if (userToken && !tokenUtils.isTokenValid(userToken)) {
        console.log('🔥 일반 사용자 토큰 만료 감지 - 토큰 삭제');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('user_info');
        userToken = null;
    }

    if (adminToken && !tokenUtils.isTokenValid(adminToken)) {
        console.log('🔥 관리자 토큰 만료 감지 - 토큰 삭제');
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('admin_user');
        adminToken = null;
    }

    console.log('🔍 라우터 가드:', {
        path: to.path,
        userToken: userToken ? '있음' : '없음',
        adminToken: adminToken ? '있음' : '없음'
    });

    // 🔥 1. 루트 경로 처리 (가장 우선)
    if (to.path === '/') {
        if (userToken && !adminToken) {
            // 일반 사용자만 로그인된 경우
            next('/home');
        } else if (adminToken && !userToken) {
            // 관리자만 로그인된 경우
            next('/admin');
        } else if (adminToken && userToken) {
            // 둘 다 로그인된 경우 - 관리자 우선
            next('/admin');  // 🔥 관리자 우선으로 변경
        } else {
            // 둘 다 없으면 로그인 페이지로
            next('/login');
        }
        return;
    }

    // 🔥 2. 관리자 경로 처리
    if (to.path.startsWith('/admin')) {
        if (to.path === '/admin/login') {
            // 관리자 로그인 페이지
            if (adminToken) {
                next('/admin');
            } else {
                next();
            }
            return;
        } else {
            // 기타 관리자 페이지
            if (!adminToken) {
                if (userToken) {
                    alert('⚠️ 관리자 전용 페이지입니다.\n일반 사용자는 접근할 수 없습니다.');
                    next('/admin/login');  // 🔥 이렇게 변경
                } else {
                    alert('⚠️ 관리자 로그인이 필요합니다.');
                    next('/admin/login');
                }
            } else {
                next();
            }
            return;
        }
    }

    // 🔥 2-2. 입점사(셀러) 경로 처리
    if (to.path.startsWith('/seller')) {
        // 🔥 2026-07-28 R3: 입점사도 고객과 동일하게 sessionStorage만 사용한다.
        //    (브라우저를 닫으면 세션이 사라져 재접속 시 항상 로그인 화면이 뜸)
        //    이전 버전에서 localStorage에 남아 있던 자동로그인 값은 즉시 정리한다.
        if (localStorage.getItem('sellerToken') || localStorage.getItem('seller_info')) {
            localStorage.removeItem('sellerToken');
            localStorage.removeItem('seller_info');
        }
        let sellerToken = sessionStorage.getItem('sellerToken');
        if (sellerToken && !tokenUtils.isTokenValid(sellerToken)) {
            // 데모 목업 토큰(demo-seller-token)은 JWT가 아니므로 만료검사에서 제외
            if (sellerToken !== 'demo-seller-token') {
                sessionStorage.removeItem('sellerToken');
                sessionStorage.removeItem('seller_info');
                sellerToken = null;
            }
        }

        // 로그인/입점신청 페이지는 누구나 접근
        if (to.path === '/seller/login' || to.path === '/seller/apply') {
            if (sellerToken && to.path === '/seller/login') {
                next('/seller');
            } else {
                next();
            }
            return;
        }
        // 그 외 입점사 페이지는 로그인 필요
        if (!sellerToken) {
            next('/seller/login');
        } else {
            next();
        }
        return;
    }

    // 🔥 2-3. 파트너 포털(영업사원) 경로 처리 — 2026-08-21 추가
    //    고객·관리자·입점사와 완전히 분리된 영역이다.
    //    토큰은 sessionStorage.partner_token 만 쓴다(브라우저를 닫으면 세션 종료).
    if (to.path.startsWith('/partner')) {
        let partnerToken = sessionStorage.getItem('partner_token');
        if (partnerToken && !tokenUtils.isTokenValid(partnerToken)) {
            sessionStorage.removeItem('partner_token');
            sessionStorage.removeItem('partner_info');
            partnerToken = null;
        }

        // 로그인 페이지는 누구나 접근
        if (to.path === '/partner/login') {
            if (partnerToken) {
                next('/partner/dashboard');
            } else {
                next();
            }
            return;
        }
        // 그 외 파트너 페이지는 로그인 필요
        if (!partnerToken) {
            next('/partner/login');
        } else {
            next();
        }
        return;
    }

    // 🔥 3. 퍼블릭 라우트 처리 (로그인, 회원가입 등)
    const publicRoutes = ['/login', '/signup', '/reset-password'];
    if (publicRoutes.includes(to.path)) {
        if (adminToken && !userToken) {
            next('/admin');
        } else if (userToken) {
            next('/home');
        } else {
            next();
        }
        return;
    }

    // 🔥 4. 일반 사용자 보호 영역 (로그인 필요)
    if (!userToken) {
        console.log('🚨 일반 사용자 토큰 없음 - 로그인 페이지로 이동');
        next('/login');
        return;
    }

    // 🔥 5. 그 외 모든 경우 통과
    next();
});


// 청크 로딩 오류 처리를 위한 전역 이벤트 리스너 추가
window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
        console.error('🚨 청크 로딩 오류 발생:', event.reason);
        // 캐시 삭제
        if ('caches' in window) {
            caches.keys().then(function(names) {
                for (let name of names) caches.delete(name);
            });
        }
        // 페이지 새로고침
        window.location.reload(true);
    }
});

export default router